// src/pages/lkp/LookupWorkbench.tsx
//
// Top-level lookup workflow — the 3 steps, with the lookup ENTITY as the spine:
//   Step 1 · Discovery  discover lookups from profiling + agent; accept/reject,
//                        auto-named + consolidated into entities. NO values decided.
//   Step 2 · Reference   (optional) ingest the client workbook, confirm mappings,
//                        fold client values onto entities as canonical evidence.
//   Step 3 · Finalize    per-entity: author canonical values + source→canonical
//                        crosswalk; assign/merge entities; publish.
//
// Mount THIS in MyComponentLazy (same props as before). It composes the per-step
// components; each step lives in its own file.

import React, { useCallback, useEffect, useState } from "react";
import { lookup_getOverview, type LookupOverview } from "../api/lkpAPI";
import { lookup_rebuildEntities } from "../api/lkpEntityAPI";
import Step1Review from "./LookupStep1Review";
import LookupReferenceStep from "./LookupReferenceStep";
import LookupEntityWorkspace from "./LookupEntityWorkspace";
import PublishReferenceButton from "./PublishReferenceButton";
import { injectLookupTheme } from "./lookupTheme";
import { lookup_downloadReview } from "../api/lkpAPI";
injectLookupTheme();

interface Props { appNm: string; entityId: string; entityLabel?: string; }
type Step = 1 | 2 | 3;

export default function LookupWorkbench({ appNm, entityId, entityLabel }: Props) {
  const [step, setStep] = useState<Step>(1);
  const [expOpen, setExpOpen] = useState(false);
  const doExport = (fmt: "xlsx" | "html" | "csv") => { setExpOpen(false); lookup_downloadReview(entityId, fmt, appNm).catch(() => {}); };
  const [ov, setOv] = useState<LookupOverview | null>(null);
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);
  const stepInit = React.useRef(false);
  const note = (m: string) => { setFlash(m); setTimeout(() => setFlash(null), 3500); };

  const loadOverview = useCallback(async () => {
    try {
      const r = await lookup_getOverview(entityId); setOv(r);
      if (!stepInit.current) {
        stepInit.current = true;
        setStep(r.step3?.present ? 3 : r.step2?.present ? 2 : 1);
      }
    } catch { setOv(null); }
  }, [entityId]);
  useEffect(() => { loadOverview(); }, [loadOverview]);

  // Build/refresh the entity registry from accepted discovery (Step 1 → entities)
  const buildEntities = async (goto?: Step) => {
    setBusy(true);
    try { await lookup_rebuildEntities(entityId, false); await loadOverview(); note("Entities built from discovery."); if (goto) setStep(goto); }
    catch { note("Build failed — run detect first."); } finally { setBusy(false); }
  };

  const STEPS: Array<{ n: Step; t: string; d: string; done: boolean }> = [
    { n: 1, t: "Discovery", d: "profiling + agent", done: !!ov?.step1?.present },
    { n: 2, t: "Reference", d: "client workbook · optional", done: !!ov?.step2?.present },
    { n: 3, t: "Finalize", d: "canonical + crosswalk", done: !!ov?.step3?.present },
  ];

  return (
    <div className="lwb">
      <style>{CSS}</style>

      <div className="lwb-bar">
        <div className="lwb-sp" />
        <PublishReferenceButton entityId={entityId} onDone={() => loadOverview()} />
        <div className="lwb-export">
          <button className="lwb-xbtn" onClick={() => setExpOpen((o) => !o)}>Export ▾</button>
          {expOpen && (
            <div className="lwb-xmenu" onMouseLeave={() => setExpOpen(false)}>
              <button onClick={() => doExport("xlsx")}>Excel (.xlsx)</button>
              <button onClick={() => doExport("html")}>HTML</button>
              <button onClick={() => doExport("csv")}>CSV</button>
            </div>
          )}
        </div>
      </div>

      <div className="lwb-steps">
        {STEPS.map((s, i) => (
          <React.Fragment key={s.n}>
            <button className={"lwb-step " + (step === s.n ? "on " : "") + (s.done ? "done" : "")} onClick={() => setStep(s.n)}>
              <span className="lwb-n">{s.done && step !== s.n ? "✓" : s.n}</span>
              <span className="lwb-txt"><b>Step {s.n} · {s.t}</b><span>{s.d}</span></span>
            </button>
            {i < STEPS.length - 1 && <span className={"lwb-link " + (STEPS[i + 1].done ? "lit" : "")} />}
          </React.Fragment>
        ))}
      </div>

      {flash && <div className="lwb-flash">{flash}</div>}

      <div className="lwb-body">
        {step === 1 && (
          <div>
            <div className="lwb-hint">
              Discover true lookup columns, code↔display pairs and dependent lookups. Accept the real ones —
              they're auto-named and consolidated into lookup entities. Final code-values are decided in Step 3.
            </div>
            <Step1Review entityId={entityId} onAccepted={loadOverview} />
            <div className="lwb-nav">
              <button className="lwb-btn" disabled={busy} onClick={() => buildEntities()}>{busy ? "…" : "Build / refresh entities"}</button>
              <div className="lwb-sp" />
              <button className="lwb-btn ghost" onClick={() => setStep(2)}>Next: Reference →</button>
              <button className="lwb-btn dark" onClick={() => buildEntities(3)}>Skip to Finalize →</button>
            </div>
          </div>
        )}

        {step === 2 && (
          <LookupReferenceStep appNm={appNm} entityId={entityId}
            onGoDiscover={() => setStep(1)} onFolded={() => { loadOverview(); setStep(3); }} />
        )}

        {step === 3 && (
          <LookupEntityWorkspace appNm={appNm} entityId={entityId} entityLabel={entityLabel} onGoStep={(n) => setStep(n)} />
        )}
      </div>
    </div>
  );
}

const CSS = `
.lwb{--ink:#14202b;--surface:#fff;--surface2:#f7f9f7;--line:#dde3df;--tx:#182430;--tx2:#5d6d68;--tx3:#8a9791;
  --gov:#0e8a6e;--gov-bg:#e2f2ec;--infer:#4a51c0;
  font-family:'Inter',system-ui,sans-serif;color:var(--tx);font-size:14px}
.lwb *{box-sizing:border-box}
.lwb .brand{font-weight:700;font-size:15px;display:flex;align-items:center;gap:9px}
.lwb .brand .dot{width:9px;height:9px;border-radius:2px;background:var(--gov);box-shadow:0 0 0 3px rgba(14,138,110,.25)}
.lwb .brand small{font-weight:400;color:#93a69d}
.lwb-bar{display:flex;align-items:center;gap:10px;padding:2px 2px 10px}
.lwb-export{position:relative}
.lwb-xbtn{border:1px solid var(--line);background:var(--surface);border-radius:8px;padding:8px 14px;font-size:12.5px;font-weight:600;cursor:pointer;font-family:inherit;color:var(--tx)}
.lwb-xbtn:hover{border-color:var(--tx2);background:var(--surface2)}
.lwb-xmenu{position:absolute;right:0;top:calc(100% + 4px);background:var(--surface);border:1px solid var(--line);border-radius:9px;box-shadow:0 8px 24px rgba(20,32,43,.12);z-index:20;min-width:150px;overflow:hidden}
.lwb-xmenu button{display:block;width:100%;text-align:left;border:none;background:transparent;padding:9px 13px;font-size:12.5px;cursor:pointer;font-family:inherit;color:var(--tx)}
.lwb-xmenu button:hover{background:var(--surface2)}
.lwb-steps{display:flex;align-items:center;background:var(--surface);border:1px solid var(--line);border-radius:12px 12px 0 0;padding:12px 16px;gap:2px}
.lwb-step{display:flex;align-items:center;gap:11px;background:transparent;border:1px solid transparent;border-radius:10px;padding:9px 14px;cursor:pointer;font-family:inherit}
.lwb-step:hover{background:var(--surface2)}.lwb-step.on{background:var(--surface2);border-color:var(--line)}
.lwb-n{width:27px;height:27px;border-radius:50%;display:grid;place-items:center;font-weight:700;font-size:13px;background:var(--surface2);border:1.5px solid var(--line);color:var(--tx3);font-family:'IBM Plex Mono',monospace}
.lwb-step.on .lwb-n{background:var(--ink);color:#fff;border-color:var(--ink)}
.lwb-step.done .lwb-n{background:var(--gov);border-color:var(--gov);color:#fff}
.lwb-step.on.done .lwb-n{background:var(--ink);border-color:var(--ink)}
.lwb-txt{display:flex;flex-direction:column;line-height:1.25;text-align:left}
.lwb-txt b{font-size:13.5px}.lwb-txt span{font-size:11px;color:var(--tx3)}
.lwb-link{flex:1;height:2px;background:var(--line);margin:0 8px;min-width:24px}.lwb-link.lit{background:var(--gov)}
.lwb-flash{padding:9px 14px;font-size:13px;background:var(--gov-bg);color:var(--gov);border-left:3px solid var(--gov)}
.lwb-body{background:var(--surface2);border:1px solid var(--line);border-top:none;border-radius:0 0 12px 12px;padding:18px}
.lwb-hint{font-size:13px;color:var(--tx2);background:var(--surface);border:1px solid var(--line);border-radius:10px;padding:11px 14px;margin-bottom:14px}
.lwb-nav{display:flex;align-items:center;gap:9px;margin-top:16px;padding-top:14px;border-top:1px dashed var(--line);flex-wrap:wrap}
.lwb-sp{flex:1}
.lwb-btn{border:1px solid var(--line);background:var(--surface);border-radius:8px;padding:9px 15px;font-size:12.5px;font-weight:500;cursor:pointer;font-family:inherit;color:var(--tx)}
.lwb-btn.ghost{background:var(--surface)}.lwb-btn.dark{background:var(--ink);color:#fff;border-color:var(--ink)}.lwb-btn.dark:hover{background:#20303e}
.lwb-btn:disabled{opacity:.5;cursor:default}
`;
